import { Fragment, useCallback, useState, useMemo, useEffect } from 'react'

import {
  Close as CloseIcon,
  CardGiftcardOutlined as RewardNGCIcon,
  LocalCafeOutlined as RewardIcon,
} from '@mui/icons-material'
import { Button, Card, Collapse, Grid, Typography } from '@mui/material'
import { get, isEmpty, shuffle, uniqBy } from 'lodash'

import {
  SparckConfirmGiftCardRewardDetails,
  SparckConfirmVirginExperiencesRewardDetails,
  SparckRetailerRewardsList,
  SparckOrganizationalRewardsList,
} from '..'
import {
  DialogTeleport,
  OrganizationalRewardCard,
  GiftCardRewardCard,
  VirginExperiencesRewardCard,
} from 'components'
import { initialDialog } from 'constants/dialog'
import { useCompany } from 'contexts'
import { RewardProvider, RewardType } from 'enums/reward'
import { WishType } from 'enums/wish'
import { t } from 'i18n'
import { formatToUSD } from 'utils/currency'
import { getRewardName } from 'utils/reward'
import { getUserWishesByWishType } from 'utils/wishlist'

const displayRetailerRewards = (company, sparckState) =>
  sparckState?.event?.has_retailer_reward && get(company, 'rewards_system')

const displayOrganizationalRewards = (company, sparckState) =>
  sparckState?.event?.has_company_reward &&
  !sparckState?.event?.automatically_bank_company_reward &&
  get(company, 'rewards_system')

const REWARDS_PREVIEW_COUNT = 5

export default function SparckRewardsSections(props) {
  const {
    sparckState,
    setSparckState,
    rewards,
    rewardsNGC,
    showMessageInput,
    topFiveWishlistedRewards,
  } = props

  const { company } = useCompany()!

  const [
    confirmGiftCardRewardDetailsDialog,
    setConfirmGiftCardRewardDetailsDialog,
  ] = useState(initialDialog)
  const [
    confirmVirginExperiencesRewardDetailsDialog,
    setConfirmVirginExperiencesRewardDetailsDialog,
  ] = useState(initialDialog)

  const [retailerRewardsDialogOpen, setRetailerRewardsDialogOpen] =
    useState(false)
  const [organizationalRewardsDialogOpen, setOrganizationalRewardsDialogOpen] =
    useState(false)

  const [retailerRewardsPreview, setRetailerRewardsPreview] = useState<any[]>(
    [],
  )
  const [organizationalRewardsPreview, setOrganizationalRewardsPreview] =
    useState<any[]>([])

  const selectedOrganizationalReward = sparckState.rewards?.find(
    reward => reward?.type === RewardType.Company,
  )
  const selectedRetailerReward = sparckState.rewards?.find(
    reward => reward?.type === RewardType.Retailer,
  )

  const handleRetailerRewardSelect = reward => {
    if (reward?.brand?.id) {
      setConfirmGiftCardRewardDetailsDialog({ isOpen: true, data: reward })
    }

    if (reward?.sku) {
      setConfirmVirginExperiencesRewardDetailsDialog({
        isOpen: true,
        data: reward,
      })
    }
  }

  const handleOrganizationalRewardSelect = useCallback(
    reward => {
      const newRewards = sparckState.rewards.filter(
        reward => reward.type !== RewardType.Company,
      )

      setSparckState({
        ...sparckState,
        rewards: [
          ...newRewards,
          {
            provider: RewardProvider.Company,
            type: RewardType.Company,
            product_id: reward.id,
            amount: reward.amount,
            details: reward,
          },
        ],
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sparckState],
  )

  const handleRewardByType = useCallback(
    rewardType => {
      const newRewards = sparckState.rewards.filter(
        reward => reward.type !== rewardType,
      )

      setSparckState({
        ...sparckState,
        rewards: newRewards,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sparckState],
  )

  const getRewardsPreview = useCallback(
    type => {
      if (
        sparckState.event_id &&
        sparckState?.individual_to_recognize?.id &&
        topFiveWishlistedRewards
      ) {
        const rewardsList = type === 'organizational' ? rewards : rewardsNGC
        const wishType =
          type === 'organizational' ? WishType.Company : WishType.Retailer

        const topOrganizationRewards = get(
          topFiveWishlistedRewards,
          `data.company.${type}`,
          [],
        )

        const topSparckRewards = get(
          topFiveWishlistedRewards,
          `data.sparck.${type}`,
          [],
        )

        const userToRecognizeWishedRewardsIds = getUserWishesByWishType(
          sparckState?.individual_to_recognize,
          wishType,
        )?.map(wish => +wish.wishable_id)

        const wishedRewards = shuffle(
          rewardsList?.filter(reward =>
            userToRecognizeWishedRewardsIds?.includes(+reward.id),
          ),
        )
        const rewardsListMaxAmount = rewardsList


        const rewardsPreview = uniqBy(
          [
            ...wishedRewards,
            ...topOrganizationRewards,
            ...(type === 'retailer' ? topSparckRewards : []),
            ...rewardsListMaxAmount,
          ],
          'id',
        ).slice(0, REWARDS_PREVIEW_COUNT)

        return rewardsPreview
      }

      return []
    },
    [
      rewards,
      rewardsNGC,
      sparckState.event_id,
      sparckState?.individual_to_recognize,
      topFiveWishlistedRewards,
    ],
  )

  const rewardsSections = useMemo(
    () => [
      {
        id: 'retailerRewards',
        selectedReward: selectedRetailerReward,
        displayRewards: displayRetailerRewards,
        title: 'Retailer Rewards',
        list: rewardsNGC,
        listPreview: retailerRewardsPreview ?? [],
        Icon: RewardNGCIcon,
        getRewardCard: selectedReward =>
          selectedReward?.details?.brand?.id || selectedReward?.brand?.id
            ? GiftCardRewardCard
            : VirginExperiencesRewardCard,
        onSelectReward: handleRetailerRewardSelect,
        onRemoveReward: () => handleRewardByType(RewardType.Retailer),
        rewardsDialogOpen: retailerRewardsDialogOpen,
        setRewardsDialogOpen: setRetailerRewardsDialogOpen,
        RewardsDialog: SparckRetailerRewardsList,
      },
      {
        id: 'organizationalRewards',
        selectedReward: selectedOrganizationalReward,
        displayRewards: displayOrganizationalRewards,
        title: 'Organizational Rewards',
        list: rewards,
        listPreview: rewards ?? [],
        Icon: RewardIcon,
        getRewardCard: () => OrganizationalRewardCard,
        onSelectReward: handleOrganizationalRewardSelect,
        onRemoveReward: () => handleRewardByType(RewardType.Company),
        rewardsDialogOpen: organizationalRewardsDialogOpen,
        setRewardsDialogOpen: setOrganizationalRewardsDialogOpen,
        RewardsDialog: SparckOrganizationalRewardsList,
      },
      // {
      //   id: 'corporateRewards',
      //   selectedReward: selectedOrganizationalReward,
      //   displayRewards: displayOrganizationalRewards,
      //   title: 'Corporate  Rewards',
      //   list: surveyRewardIntentions,
      //   listPreview: surveyRewardIntentions ?? [],
      //   Icon: RewardIcon,
      //   getRewardCard: () => CorporateRewardCard,
      //   onSelectReward: handleOrganizationalRewardSelect,
      //   onRemoveReward: () => handleRewardByType(RewardType.Company),
      //   rewardsDialogOpen: organizationalRewardsDialogOpen,
      //   setRewardsDialogOpen: setOrganizationalRewardsDialogOpen,
      //   RewardsDialog: SparckOrganizationalRewardsList,
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleOrganizationalRewardSelect,
      handleRewardByType,
      organizationalRewardsDialogOpen,
      organizationalRewardsPreview,
      retailerRewardsDialogOpen,
      retailerRewardsPreview,
      rewards,
      rewardsNGC,
      selectedOrganizationalReward,
      selectedRetailerReward,
    ],
  )

  useEffect(() => {
    if (
      retailerRewardsPreview?.length === 0 &&
      (rewardsNGC?.length > 0 || topFiveWishlistedRewards?.length > 0)
    ) {
      setRetailerRewardsPreview([...getRewardsPreview('retailer')])
    }

    if (
      organizationalRewardsPreview?.length === 0 &&
      (rewards?.length > 0 || topFiveWishlistedRewards?.length > 0)
    ) {
      setOrganizationalRewardsPreview([...getRewardsPreview('organizational')])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sparckState.individual_to_recognize,
    sparckState.event_id,
    rewards,
    rewardsNGC,
    topFiveWishlistedRewards,
  ])

  return (
    !isEmpty(sparckState.users_to_recognize) &&
    sparckState.event_id &&
    showMessageInput && (
      <>
        <Grid item xs={12}>
          <Collapse
            in={
              !isEmpty(sparckState.users_to_recognize) &&
              sparckState.event_id &&
              showMessageInput
            }
            timeout={600}
          >
            <Grid container justifyContent='center' spacing={2} sx={{ my: 2 }}>
              {rewardsSections && rewardsSections.map(
                ({
                  id,
                  selectedReward,
                  displayRewards,
                  title,
                  listPreview,
                  Icon,
                  getRewardCard,
                  onSelectReward,
                  onRemoveReward,
                  rewardsDialogOpen,
                  setRewardsDialogOpen,
                  RewardsDialog,
                }) => {
                  if (id === "retailerRewards") return false;

                  const RewardCard = getRewardCard(selectedReward)

                  return (
                    displayRewards(company, sparckState) && (
                      <Fragment key={id}>
                        {selectedReward ? (
                          <Grid item xs={12} md={6} lg={4} key={id}>
                            <Card>
                              <Grid
                                container
                                direction='row'
                                sx={{ height: 1 }}
                              >
                                <Grid item xs={12}>
                                  <RewardCard
                                    reward={selectedReward.details}
                                    cardSx={{
                                      aspectRatio: '16/9',
                                      width: '100%',
                                    }}
                                  />
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  sx={{ mt: 2, mb: 0.5, px: 2 }}
                                >
                                  <Typography
                                    variant='body1'
                                    fontWeight={500}
                                    align='center'
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    <Typography
                                      component='span'
                                      variant='h6'
                                      fontWeight={700}
                                    >
                                      {formatToUSD(selectedReward?.amount)}{' '}
                                    </Typography>
                                    {getRewardName(selectedReward?.details)
                                      ? getRewardName(selectedReward?.details)
                                      : selectedReward?.details?.brand?.name ??
                                      t('sparcks.sparck.selectedReward')}{' '}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} sx={{ mb: 1 }}>
                                  <Grid
                                    container
                                    spacing={2}
                                    justifyContent='center'
                                  >
                                    <Grid item>
                                      <Button
                                        color='error'
                                        onClick={onRemoveReward}
                                      >
                                        <CloseIcon
                                          sx={{ fontSize: '1.15em', mr: 0.25 }}
                                        />{' '}
                                        Remove
                                      </Button>
                                    </Grid>

                                    <Grid item>
                                      <Button
                                        color='info'
                                        onClick={() =>
                                          setRewardsDialogOpen(true)
                                        }
                                      >
                                        {t('sparcks.sparck.chooseAnother')}
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Card>
                          </Grid>
                        ) : (
                          <Grid item xs={12} key={id}>
                            <RewardsTrendingList
                              id={id}
                              title={title}
                              Icon={Icon}
                              listPreview={listPreview}
                              getRewardCard={getRewardCard}
                              onSelectReward={onSelectReward}
                              setRewardsDialogOpen={setRewardsDialogOpen}
                            />
                          </Grid>
                        )}

                        <DialogTeleport
                          dialogOpen={rewardsDialogOpen}
                          handleDialogClose={() => setRewardsDialogOpen(false)}
                          dialogSize='lg'
                          dialogTitle={title}
                          isFullWidth
                          scroll='body'
                        >
                          <RewardsDialog
                            {...props}
                            onSelectReward={reward => {
                              onSelectReward(reward)
                              setRewardsDialogOpen(false)
                            }}
                          />
                        </DialogTeleport>
                      </Fragment>
                    )
                  )
                },
              )}

              {sparckState?.event?.automatically_bank_company_reward && (
                <Grid item xs={12}>
                  <Typography
                    variant='body1'
                    fontWeight={500}
                    color='secondary'
                    align='center'
                    paragraph
                  >
                    {t(
                      'sparcks.sparck.selectedRecognitionEventAutomaticBankWarning',
                    )}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Collapse>
        </Grid>

        <DialogTeleport
          dialogOpen={confirmGiftCardRewardDetailsDialog.isOpen}
          dialogSize='xs'
          dialogTitle={t('sparcks.sparck.giftCardRewardDetails')}
          handleDialogClose={() =>
            setConfirmGiftCardRewardDetailsDialog(initialDialog)
          }
        >
          <SparckConfirmGiftCardRewardDetails
            reward={confirmGiftCardRewardDetailsDialog?.data}
            onConfirmReward={reward => {
              const newRewards = sparckState.rewards.filter(
                reward => reward.type !== RewardType.Retailer,
              )
              setSparckState({
                ...sparckState,
                rewards: [...newRewards, reward],
              })

              setConfirmGiftCardRewardDetailsDialog(initialDialog)
            }}
            giftCardMaximumValue={
              +sparckState?.event?.maximum_reward_amount_for_each_recognition
            }
          />
        </DialogTeleport>

        <DialogTeleport
          dialogOpen={confirmVirginExperiencesRewardDetailsDialog.isOpen}
          dialogSize='sm'
          dialogTitle={t('sparcks.sparck.virginExperiencesRewardDetails')}
          handleDialogClose={() =>
            setConfirmVirginExperiencesRewardDetailsDialog(initialDialog)
          }
        >
          <SparckConfirmVirginExperiencesRewardDetails
            reward={confirmVirginExperiencesRewardDetailsDialog?.data}
            onConfirmReward={reward => {
              const newRewards = sparckState.rewards.filter(
                reward => reward.type !== RewardType.Retailer,
              )
              setSparckState({
                ...sparckState,
                rewards: [...newRewards, reward],
              })

              setConfirmVirginExperiencesRewardDetailsDialog(initialDialog)
            }}
            rewardMaximumValue={
              +sparckState?.event?.maximum_reward_amount_for_each_recognition
            }
          />
        </DialogTeleport>
      </>
    )
  )
}

function RewardsTrendingList({
  id,
  title,
  Icon,
  listPreview,
  getRewardCard,
  onSelectReward,
  setRewardsDialogOpen,
}) {
  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Grid
            container
            spacing={2}
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid item xs>
              <Typography component='h3' variant='body1' fontWeight={500}>
                <Grid component='span' container alignItems='center'>
                  <Icon sx={{ mr: 1 }} /> {title}
                </Grid>
              </Typography>
            </Grid>

            <Grid item xs='auto'>
              <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={() => setRewardsDialogOpen(true)}
              >
                {t('sparcks.sparck.viewAll')}
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {(listPreview ?? [])
          .filter((_, index) => index < REWARDS_PREVIEW_COUNT)
          .map((reward, index) => {
            const RewardCard = getRewardCard(reward)

            return (
              <Grid
                item
                xs={12}
                sm={index <= 1 ? 6 : 4}
                lg={listPreview?.length < REWARDS_PREVIEW_COUNT ? 3 : true}
                key={reward.id}
              >
                <RewardCard
                  id={id}
                  reward={reward}
                  onClick={onSelectReward}
                  {...(id === 'organizationalRewards' && {
                    cardSx: {
                      height: 1,
                    },
                  })}
                />
              </Grid>
            )
          })}
      </Grid>
    </>
  )
}
